import React, { useRef, useState, useEffect, useLayoutEffect } from 'react'
import { Dropdown, Form, FormControl, OverlayTrigger, Tooltip } from 'react-bootstrap';

import DropdownArrow from '../../assets/icons/Icon - Arrow - Down.svg';

import './multi-select-dropdown.scss';

// Renders a single dropdown row's text label, and only attaches the OverlayTrigger
// when the text actually overflows its container. The overflow check runs once,
// inside useLayoutEffect, against the real rendered span — so it stays accurate
// across responsive container widths (.dropdown-row is width:100% with
// max-width:1000px; .dropdown-row-text is max-width:85%). No measurement happens
// on each parent render or each keystroke, which was the original perf bug.
const DropdownRowText = ({ name }) => {
    const textRef = useRef(null);
    const [isOverflowing, setIsOverflowing] = useState(false);

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el) {
            return;
        }
        setIsOverflowing(el.scrollWidth > el.clientWidth);
    }, [name]);

    return (
        <OverlayTrigger
            trigger={isOverflowing ? ['hover', 'focus'] : []}
            placement="bottom"
            overlay={
                <Tooltip className="mutli-select-dropdown-tooltip-wrapper">
                    {name}
                </Tooltip>
            }
        >
            <span ref={textRef} className='dropdown-row-text'>
                {name}
            </span>
        </OverlayTrigger>
    );
};

// values MUST be an array in the format {id: ..., name: ..., }
//   id is the 'unique' property of each item, and name is the displayed value
//   disabled is an optional field that just greys out the option's text, and disallows the user from selecting it
const MultiSelectDropdown = ({
        placeholderText, 
        onSelect, 
        unSelectedValues = [],
        selectedValues = [], 
        dropdownStyleClassName,
        CustomDropdownToggle = null,
        customDropdownRenderer = null, 
        pluralizedText = "items",

        isSearchable = false
      }) => {
    
    // are there >= 6 total items?
    const [isScroll, setIsScroll] = useState(false);

    // Value for the searchbox
    const [searchVal, setSearchVal] = useState("");
    const [filteredSelectedValues, setFilteredSelectedValues] = useState([]);
    const [filteredUnSelectedValues, setFilteredUnSelectedValues] = useState([]);
    const [displayedValues, setDisplayedValues] = useState(0);

    // workaround to display up arrow icon when dropdown toggled, and down arrow otherwise
    const [toggled, setToggled] = useState(false);
    const dropdownRef = useRef(null);

    // Determines whether dropdown is scollable or not based on num of items
    useEffect(() => {
        const totalItemLengthTemp = selectedValues.length + unSelectedValues.length;
        setIsScroll(totalItemLengthTemp >= 6);
        if (isSearchable) {
            filterValues(searchVal);
        }
    }, [selectedValues,unSelectedValues ])

    // Handles click event for dropdown open. The listener only reads from refs and
    // setters with stable identity, so the empty dep array is intentional: re-attaching
    // the document listener on every render thrashed React perf while typing.
    useEffect(() => {
        document.addEventListener("mousedown", handleDropdownOpen);
        return () => {
            document.removeEventListener("mousedown", handleDropdownOpen);
        };
    }, [])

    // Returns classname
    const getClassName = className => className ? className : "";

    // Dropdown open function
    const handleDropdownOpen = (event) => {

        // Check if the main dropdown button was pressed
        if(dropdownRef.current.contains(event.target) && (event.target.parentNode.classList[0] == "dropdown-button" || event.target.classList[0] == "dropdown-button" )) {
            setToggled(prev => !prev);
            return;
        } else if (dropdownRef.current.contains(event.target)){
            setToggled(true);
            return;
        }
        setToggled(false);
            
    }

    // Handles sorting and displaying only selected values
    const addSelection = (value, unSelectedValuesIndex) => {

        const newSelectedValues = [...selectedValues, value];
        const newUnSelectedValues = [...unSelectedValues.slice(0, unSelectedValuesIndex), ...unSelectedValues.slice(unSelectedValuesIndex+1)]

        onSelect(sortByName(newSelectedValues), sortByName(newUnSelectedValues));
    }

    // Handles sorting and displaying only unselected values
    const removeSelection = (value, selectedValuesIndex) => {

        const newSelectedValues = [...selectedValues.slice(0,selectedValuesIndex), ...selectedValues.slice(selectedValuesIndex+1)];
        const newUnSelectedValues = [value, ...unSelectedValues];

        onSelect(sortByName(newSelectedValues), sortByName(newUnSelectedValues));
    }

    // Sorts dropdown list items by alphabetical order
    const sortByName = (list) => {
        return list.sort((itemA, itemB) => {
            let nameA = itemA.name.toLowerCase();
            let nameB = itemB.name.toLowerCase();
            if (nameA < nameB){
                return -1;
            }
            else if (nameA > nameB){
                return 1;
            }
            else{
                return 0;
            }
        })
    }

    // Identifies when the dropdown menu is being used with keyboard (manual search, down arrow)
    const onKeyDropDownToggle = (keyEvent) => {
        if ((keyEvent.charCode || keyEvent.keyCode) === 40) {
            setToggled(true);
        }
    }

    // Removes dropdown item from selected status to unselected 
    const handleCheckboxClick = (isSelected,value, index) => {
        if(isSelected){
            removeSelection(value, index);
            return;
        }

        addSelection(value, index);
    }

    // Sets the direction of the arrow SVG in the dropdown window
    const arrowDirection = (toggled) => {
        if(toggled){
            return "dropdown-up-arrow";
        }
        else{
            return "dropdown-arrow";
        }
    }

    // Loads data for dropdown menu whenever filter type is selected: Sets selected and unselected values
    // Is ONLY used when isSearchable = true (Dropdown with searchbar is being used)
    const filterValues = (e) => {
        let totalDisplayedValues = 0;
        const filterLowercased = e.toLowerCase();
        
        let mapValues = (values) => {
            return values.map(val => {
                if(val.name.toLowerCase().includes(filterLowercased)){
                    totalDisplayedValues += 1;
                    return {id: val.id, name: val.name, isFiltered: true}
                }
                return {id: val.id, name: val.name, isFiltered: false}
            });
        }

        let newSelectedValues = mapValues(selectedValues);
        let newUnSelectedValues = mapValues(unSelectedValues);

        setDisplayedValues(totalDisplayedValues);
        setFilteredSelectedValues(newSelectedValues);                                                 
        setFilteredUnSelectedValues(removeDuplicates(newUnSelectedValues, newSelectedValues));
    }

    // Component for displaying one individual dropdown item
    const displayItem = (value,index, isSelected) => {
            return(
                <div style={isSearchable ? { display: value.isFiltered  ? '' : 'none' } : {}}>
                    <Dropdown.Item
                        className={`dropdown-row ${getClassName(dropdownStyleClassName)}`}
                        key={value.id}
                        value={value.id}
                        disabled={value.disabled}
                        id={index}
                        >
                        <div
                        className = {"dropdown-item-container"}
                        >
                            <Form.Check>
                                <Form.Check.Input
                                    type="checkbox"
                                    checked={isSelected}
                                    onMouseUp={() => {handleCheckboxClick(isSelected,value, index)}}
                                />
                            </Form.Check>

                            { customDropdownRenderer !== null ?
                                customDropdownRenderer(value) :
                                <DropdownRowText name={value.name} />
                            }
                        </div>
                    </Dropdown.Item>
                    <Dropdown.Divider className={`dropdown-row-divider ${getClassName(dropdownStyleClassName)}`} />
                </div>
            )
    }

    // Removes duplicate values - in this use case, removes selected values from unselected values 
    const removeDuplicates = (array_of_tags, array_of_tags_selected) => {
  
        var newArray = [];  
        var uniqueObject = {};
          
        for (var i in array_of_tags) {
  
            var objTitle = array_of_tags[i]['name'];
            uniqueObject[objTitle] = array_of_tags[i];
        }
          
        for (i in uniqueObject) {
            if(!containsObject(i, array_of_tags_selected)){
                newArray.push(uniqueObject[i]);
            }
        }

        return(newArray);
    }

    // Determines if an item exists in a list
    const containsObject = (obj, list) => {
        var i;
        for (i = 0; i < list.length; i++) {
            if (list[i]['name'] === obj) {
                return true;
            }
        }  
        return false;
    }

    // Handles displaying text in the dropdown search bar 
    const renderSelectedValuesOnDropdown = () => {
        let searchbarText = "";
        let dropdownText = "";
        let spanStyles = {
            position: "absolute",
            top: "9px",
            color: "#333333",
            overflow: "hidden",
            width: "90%",
            textOverflow: "ellipsis"
        }

        // Text displayed in the dropdown window 
        if(selectedValues.length <= 0){
            dropdownText = placeholderText;
            spanStyles.color = "#BBBBBB";
        } else if (selectedValues.length === 1){
            dropdownText = selectedValues[0].name;
            if (searchVal == "") searchbarText = selectedValues[0].name;
        } else {
            dropdownText = `${selectedValues.length } ${pluralizedText} selected`;
            if (searchVal == "") searchbarText = `${selectedValues.length } ${pluralizedText} selected`;
        }

        return(
            <span style={spanStyles}>
                {isSearchable ? searchbarText : dropdownText}
            </span>
        )
    }
    
    // Combined component for multi select drop down
    return (
      <div 
            ref={dropdownRef}
            className="dropdown-main-multiselect-top-level" 
            >

            { // If isSearchable is TRUE, render the dropdown with the Search Bar 
            isSearchable ? (
                  
                <Dropdown
                      show = {toggled}
                      className={`dropdown-main-multiselect ${dropdownStyleClassName ? dropdownStyleClassName : ""}`} 
                      >
                      <Dropdown.Toggle 
                          // onClick={()=>{setToggled(!toggled)}}
                          onKeyDown = {onKeyDropDownToggle} 
                          tabIndex="0" 
                          id='dropdown-toggle' 
                          className={toggled ? 'dropdown-button selected-button' : 'dropdown-button'} 
                          as='div'>   

                          {renderSelectedValuesOnDropdown()}
                          
                          <img className = {arrowDirection(toggled)} alt='dropdown-arrow' src={DropdownArrow}/>
                        <FormControl
                          // The searchbar component used by the dropdown - is absolutely positioned ON-TOP of the base dropdown component
                          style={{background:"none", position:"absolute", top:0, left:0}}
                          placeholder={selectedValues.length <= 0 ? placeholderText : "" }
                          value={searchVal}
                          onChange={(e) => {setSearchVal(e.target.value); filterValues(e.target.value)}}
                        />

                      </Dropdown.Toggle>
                      
                      <Dropdown.Menu 
                          className={`dropdown-main-multiselect-menu ${isScroll ? "" : "no-scroll"}  ${dropdownStyleClassName ? dropdownStyleClassName : ""}`} as='div'>
                          

                        { // Display selected FILTERED values first (searchbar ONLY)
                        filteredSelectedValues &&
                            filteredSelectedValues.map((value,index) =>{
                                return displayItem(value,index,true)
                            })
                        }

                        { // Display unselected FILTERED values first (searchbar ONLY)
                        filteredUnSelectedValues  &&
                            filteredUnSelectedValues.map((value,index) =>{
                                return displayItem(value,index,false)
                            })
                        }       
                        { // If no items match the current input text, then display "No Results Found" in dropdown
                        (displayedValues === 0) && 
                            <Dropdown.Item className={`dropdown-row ${dropdownStyleClassName ? dropdownStyleClassName : ""}`}>
                                <div className = "dropdown-item-container">
                                    <span className='dropdown-row-text'>
                                        No Results Found
                                    </span> 
                                </div>
                            </Dropdown.Item>
                        }
                          
                      </Dropdown.Menu>
                  </Dropdown>
                ) 
                : 
                (
                  // ELSE render the dropdown WITHOUT the search bar (default behaviour)
                  <Dropdown
                      show = {toggled}
                      className={`dropdown-main-multiselect ${getClassName(dropdownStyleClassName)}`} 
                      >
                      <Dropdown.Toggle 
                          // onClick={()=>{setToggled(!toggled)}}
                          onKeyDown = {onKeyDropDownToggle} 
                          tabIndex="0" 
                          id='dropdown-toggle' 
                          className='dropdown-button' 
                          as='div'>   

                          {
                              CustomDropdownToggle !== null ?
                                  CustomDropdownToggle :
                                  renderSelectedValuesOnDropdown()
                          }

                          <img className = {arrowDirection(toggled)} alt='dropdown-arrow' src={DropdownArrow}/>

                      </Dropdown.Toggle>

                      <Dropdown.Menu className={`dropdown-main-multiselect-menu ${isScroll ? "" : "no-scroll"}  ${getClassName(dropdownStyleClassName)}`} as='div'>
                          {
                          // Render all SELECTED values in dropdown FIRST
                          selectedValues &&
                              selectedValues.map((value,index) =>{
                                if (value.id == undefined) {
                                    return displayItem({id: index, name: value}, index, true)
                                } else {
                                    return displayItem(value, index, true)
                                }
                              })
                          }

                          { // Render UNSELECTED values in dropdown AFTER
                          unSelectedValues &&    
                              unSelectedValues.map((value, index) => {
                                if (value.id == undefined) {
                                    return displayItem({id: index, name: value}, index, false)
                                } else {
                                    return displayItem(value, index, false)
                                }
                              })
                          }
                      </Dropdown.Menu>
                  </Dropdown>
                )
            }

        </div>
    )
}

export default MultiSelectDropdown;